import React, { useCallback, useEffect, useState } from "react";
import { Boxes, Cpu, Users, GitBranch, Play, Zap, FileDown, GitCompare, ChevronRight, ChevronLeft, ChevronUp, ChevronDown, X, Sparkles, HelpCircle, TrendingUp, TrendingDown, Minus, ShieldAlert, Pin, Trash2, Target } from "lucide-react";
import api from "../../lib/api";

const bandCls = (b) => b === "Advanced" ? "text-emerald-700" : b === "Developing" ? "text-amber-700" : "text-zinc-600";

const TIER_CLS = {
  validated: "text-emerald-700 bg-emerald-50 border-emerald-200",
  committed: "text-orange-700 bg-orange-50 border-orange-200",
  indicative: "text-indigo-700 bg-indigo-50 border-indigo-200",
};
function ConfidenceBadge({ conf }) {
  if (!conf) return null;
  return (
    <span data-testid="twin-confidence-badge" title={conf.note}
      className={`text-[10px] font-semibold border rounded px-1.5 py-0.5 ml-1.5 ${TIER_CLS[conf.tier] || TIER_CLS.indicative}`}>
      {conf.label}
    </span>
  );
}

function ConfidenceLegend() {
  const items = [
    ["validated", "Validated", "independently validated"],
    ["committed", "Committed", "owner-signed, not yet validated"],
    ["indicative", "Indicative", "apportioned from the value base"],
  ];
  return (
    <div data-testid="twin-confidence-legend" className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 pt-2 border-t border-zinc-200 text-[10.5px] text-zinc-500">
      <span className="font-semibold text-zinc-600">Confidence key:</span>
      {items.map(([tier, label, note]) => (
        <span key={tier} className="inline-flex items-center gap-1">
          <span className={`text-[9.5px] font-semibold border rounded px-1 py-0.5 ${TIER_CLS[tier]}`}>{label}</span>
          <span>{note}</span>
        </span>
      ))}
    </div>
  );
}

function TrendPill({ trend }) {
  if (!trend || trend.delta_gbp == null) return null;
  const gbp = (n) => { n = Math.abs(n); return n >= 1e6 ? `£${(n / 1e6).toFixed(1)}m` : n >= 1e3 ? `£${Math.round(n / 1e3)}k` : `£${n}`; };
  const cfg = {
    falling: ["text-emerald-700", TrendingDown, "down"],
    rising: ["text-red-700", TrendingUp, "up"],
    flat: ["text-zinc-500", Minus, "flat"],
  }[trend.direction] || ["text-zinc-500", Minus, "flat"];
  const [cls, Icon, word] = cfg;
  return (
    <span data-testid="twin-exposure-trend" className={`inline-flex items-center gap-1 text-[12px] font-semibold ${cls} ml-2`}>
      <Icon size={13} /> {word} ~{gbp(trend.delta_gbp)} vs {trend.prev_week}
    </span>
  );
}

function Sparkline({ series, plan }) {
  const pts = (series || []).filter((s) => s.gbp != null);
  if (pts.length < 2) return null;
  const vals = pts.map((p) => p.gbp);
  const planVals = plan && plan.length === vals.length ? plan : null;
  const domain = vals.concat(planVals || []);
  const lo = Math.min(...domain), hi = Math.max(...domain), rng = (hi - lo) || 1;
  const w = 132, h = 30, pad = 3, step = (w - 2 * pad) / (vals.length - 1);
  const toCoords = (arr) => arr.map((v, i) => [pad + i * step, h - pad - ((v - lo) / rng) * (h - 2 * pad)]);
  const coords = toCoords(vals);
  const poly = coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [lx, ly] = coords[coords.length - 1];
  const falling = vals[vals.length - 1] <= vals[0];
  const stroke = falling ? "#059669" : "#dc2626";
  const planPoly = planVals ? toCoords(planVals).map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ") : null;
  return (
    <svg data-testid="twin-exposure-sparkline" width={w} height={h} viewBox={`0 0 ${w} ${h}`}
      className="inline-block align-middle ml-2" role="img" aria-label="Total-at-risk trend vs plan">
      {planPoly && <polyline data-testid="twin-sparkline-plan" fill="none" stroke="#94a3b8" strokeWidth="1.2" strokeDasharray="3,2" points={planPoly} />}
      <polyline fill="none" stroke={stroke} strokeWidth="1.8" points={poly} />
      <circle cx={lx.toFixed(1)} cy={ly.toFixed(1)} r="2.4" fill={stroke} />
    </svg>
  );
}

const TOUR_KEY = "ekselens.twinTour.dismissed";


const TOUR_STEPS = [
  { title: "Meet the Digital Twin", body: "A queryable model of the target operating model, grounded in the live evidence graph. Every figure here traces back to a governed register entry — no separate data entry, no drift." },
  { title: "Portfolio at a glance", body: "The top tiles count your value streams, owners, systems and cross-stream dependencies, with portfolio maturity (current → target). It's the 10-second read before you dive in." },
  { title: "Interrogate any stream", body: "Click any value-stream card to ask who owns it, what depends on it, which systems it shares and its KPIs. Great for answering a board member's question live." },
  { title: "Run a what-if scenario", body: "Under 'What-if scenarios', pick a scenario (gate slip, decision delay, defer a stream) and a target, then Run. You get a deterministic headline plus the £ value at stake and the downstream cascade." },
  { title: "Compare two scenarios", body: "In 'Compare scenarios', set up scenario A and B and see the £ value-at-stake delta side-by-side — so SteerCo can prioritise which exposure to mitigate first." },
  { title: "Take it to the board", body: "Any scenario result has a 'Board pack' button that downloads a branded PDF. Top exposure and modelled what-ifs also fold automatically into the Delivery Status Pack." },
];

function TwinTour({ open, setOpen }) {
  const [step, setStep] = useState(0);
  const dismiss = useCallback(() => {
    try { localStorage.setItem(TOUR_KEY, "true"); } catch { /* noop */ }
    setOpen(false); setStep(0);
  }, [setOpen]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
      if (e.key === "ArrowRight" && step < TOUR_STEPS.length - 1) setStep((s) => s + 1);
      if (e.key === "ArrowLeft" && step > 0) setStep((s) => s - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step, dismiss]);

  if (!open) return null;
  const current = TOUR_STEPS[step];
  const isFirst = step === 0, isLast = step === TOUR_STEPS.length - 1;
  return (
    <div className="fixed inset-0 z-[60] bg-black/65 backdrop-blur-[2px] flex items-center justify-center px-4" data-testid="twin-tour-overlay">
      <div className="bg-white max-w-lg w-full p-7 border border-zinc-300 shadow-2xl relative rounded-lg">
        <button type="button" onClick={dismiss} className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-700" aria-label="Close tour" data-testid="twin-tour-close"><X size={18} /></button>
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[var(--e360-primary)]" />
          <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--e360-primary)]">Digital Twin tour · {step + 1} / {TOUR_STEPS.length}</div>
        </div>
        <h3 className="text-xl font-semibold text-zinc-900 mt-3" data-testid="twin-tour-title">{current.title}</h3>
        <p className="text-[14.5px] text-zinc-700 mt-3 font-light leading-relaxed">{current.body}</p>
        <div className="mt-7 flex items-center justify-between">
          <button type="button" onClick={dismiss} className="text-[12.5px] text-zinc-500 hover:text-zinc-800" data-testid="twin-tour-skip">Skip tour</button>
          <div className="flex gap-2">
            {!isFirst && <button type="button" onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-1 px-4 py-2 border border-zinc-300 text-zinc-800 hover:bg-zinc-50 text-[13px] rounded" data-testid="twin-tour-prev"><ChevronLeft size={14} /> Back</button>}
            {!isLast
              ? <button type="button" onClick={() => setStep((s) => s + 1)} className="inline-flex items-center gap-1 px-4 py-2 text-[13px] rounded bg-[var(--e360-primary)] text-white" data-testid="twin-tour-next">Next <ChevronRight size={14} /></button>
              : <button type="button" onClick={dismiss} className="inline-flex items-center gap-1 px-4 py-2 text-[13px] rounded bg-[var(--e360-primary)] text-white" data-testid="twin-tour-finish">Got it</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ScenarioPicker({ label, scenarios, options, sid, setSid, target, setTarget, testid }) {
  const targets = options[sid] || [];
  return (
    <div className="border border-zinc-200 rounded-lg p-3 flex-1 min-w-[240px]">
      <div className="text-[11px] font-semibold text-zinc-500 mb-2">{label}</div>
      <div className="flex flex-col gap-2">
        <select data-testid={`${testid}-scenario`} value={sid} onChange={(e) => { setSid(e.target.value); setTarget(""); }}
          className="text-[12px] border border-zinc-300 rounded px-2 py-1.5 bg-white">
          {scenarios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select data-testid={`${testid}-target`} value={target} onChange={(e) => setTarget(e.target.value)}
          className="text-[12px] border border-zinc-300 rounded px-2 py-1.5 bg-white">
          <option value="">Select target…</option>
          {targets.map((t) => <option key={t.ref || t.id} value={t.ref || t.id}>{t.title || t.name}</option>)}
        </select>
      </div>
    </div>
  );
}

function paramsFor(sid, target) {
  return sid === "gate_slip" ? { gate_ref: target, slip_days: 30 }
    : sid === "decision_delay" ? { decision_ref: target } : { stream_id: target };
}

function ScenarioCompare({ clientId, scenarios, options }) {
  const [aSid, setASid] = useState(scenarios[0]?.id || "");
  const [aTarget, setATarget] = useState("");
  const [bSid, setBSid] = useState(scenarios[1]?.id || scenarios[0]?.id || "");
  const [bTarget, setBTarget] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pins, setPins] = useState([]);
  const [pinName, setPinName] = useState("");
  const [pinning, setPinning] = useState(false);

  const loadPins = useCallback(() => {
    api.get(`/admin/enterprise360/clients/${clientId}/saved-comparisons`)
      .then(({ data }) => setPins(data.items || [])).catch(() => {});
  }, [clientId]);
  useEffect(() => { loadPins(); }, [loadPins]);

  const run = useCallback(async () => {
    if (!aTarget || !bTarget) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/admin/enterprise360/clients/${clientId}/digital-twin/compare`, {
        a: { scenario: aSid, params: paramsFor(aSid, aTarget) },
        b: { scenario: bSid, params: paramsFor(bSid, bTarget) },
      });
      setResult(data);
    } catch { setResult({ error: true }); }
    setBusy(false);
  }, [clientId, aSid, aTarget, bSid, bTarget]);

  const pin = useCallback(async () => {
    if (!pinName.trim() || !aTarget || !bTarget) return;
    setPinning(true);
    try {
      await api.post(`/admin/enterprise360/clients/${clientId}/saved-comparisons`, {
        name: pinName.trim(),
        a: { scenario: aSid, params: paramsFor(aSid, aTarget) },
        b: { scenario: bSid, params: paramsFor(bSid, bTarget) },
      });
      setPinName(""); loadPins();
    } catch { /* ignore */ }
    setPinning(false);
  }, [clientId, pinName, aSid, aTarget, bSid, bTarget, loadPins]);

  const unpin = useCallback(async (id) => {
    try { await api.delete(`/admin/enterprise360/clients/${clientId}/saved-comparisons/${id}`); loadPins(); }
    catch { /* ignore */ }
  }, [clientId, loadPins]);

  const movePin = useCallback(async (index, dir) => {
    const next = [...pins];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setPins(next);
    try { await api.put(`/admin/enterprise360/clients/${clientId}/saved-comparisons/reorder`, { order: next.map((p) => p.id) }); }
    catch { loadPins(); }
  }, [clientId, pins, loadPins]);

  const boardPack = useCallback(async () => {
    const qs = new URLSearchParams({ scenario: aSid, target: aTarget, scenario_b: bSid, target_b: bTarget });
    try {
      const res = await api.get(`/admin/enterprise360/clients/${clientId}/status-pack.pdf?${qs.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Delivery_Status_Pack.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  }, [clientId, aSid, aTarget, bSid, bTarget]);

  const Side = ({ r, val, higher, testid }) => (
    <div data-testid={testid} className={`rounded-lg p-3 border ${higher ? "border-[var(--e360-primary)] bg-[var(--e360-primary)]/5" : "border-zinc-200 bg-white"}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-semibold text-zinc-500">{r.scenario_label} · {r.target_label}</span>
        {higher && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--e360-primary)] text-white">Higher exposure</span>}
      </div>
      <p className="text-[12px] text-zinc-800 mb-1">{r.headline}</p>
      {val && <p className="text-[13px] font-semibold text-[var(--e360-primary)]">Value at stake: ~{val}<ConfidenceBadge conf={r.confidence} /></p>}
    </div>
  );

  return (
    <div data-testid="twin-compare" className="border border-zinc-200 rounded-lg p-3 mt-6">
      <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] mb-2">
        <GitCompare size={12} className="inline mr-1" /> Compare scenarios
      </div>
      <p className="text-[11px] text-zinc-400 mb-3">Set up two what-ifs and see the £ value-at-stake delta side-by-side — to decide which exposure to mitigate first.</p>
      {pins.length > 0 && (
        <div data-testid="twin-pinned-comparisons" className="mb-3 border border-[var(--e360-primary)]/20 bg-[var(--e360-primary)]/5 rounded-lg p-2.5">
          <div className="text-[11px] font-semibold text-zinc-500 mb-1.5"><Pin size={11} className="inline mr-1" /> Pinned comparisons — auto-included in every status pack</div>
          <ul className="space-y-1">
            {pins.map((p, i) => (
              <li key={p.id} data-testid={`twin-pin-${p.id}`} className="flex items-center justify-between gap-2 text-[12px] text-zinc-700">
                <span className="flex items-center gap-1.5">
                  <span className="text-zinc-400 font-mono text-[10px]">{i + 1}.</span> {p.name}
                </span>
                <span className="flex items-center gap-1">
                  <button data-testid={`twin-pin-up-${p.id}`} onClick={() => movePin(i, -1)} disabled={i === 0}
                    title="Move up" className="text-zinc-400 hover:text-[var(--e360-primary)] disabled:opacity-30"><ChevronUp size={14} /></button>
                  <button data-testid={`twin-pin-down-${p.id}`} onClick={() => movePin(i, 1)} disabled={i === pins.length - 1}
                    title="Move down" className="text-zinc-400 hover:text-[var(--e360-primary)] disabled:opacity-30"><ChevronDown size={14} /></button>
                  <button data-testid={`twin-unpin-${p.id}`} onClick={() => unpin(p.id)} title="Remove pin"
                    className="text-zinc-400 hover:text-red-600 ml-1"><Trash2 size={13} /></button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap gap-3 items-stretch">
        <ScenarioPicker label="Scenario A" scenarios={scenarios} options={options} sid={aSid} setSid={setASid} target={aTarget} setTarget={setATarget} testid="twin-compare-a" />
        <ScenarioPicker label="Scenario B" scenarios={scenarios} options={options} sid={bSid} setSid={setBSid} target={bTarget} setTarget={setBTarget} testid="twin-compare-b" />
      </div>
      <button data-testid="twin-compare-run" onClick={run} disabled={!aTarget || !bTarget || busy}
        className="inline-flex items-center gap-1 text-[12px] px-3 py-1.5 rounded bg-[var(--e360-primary)] text-white disabled:opacity-40 mt-3">
        <GitCompare size={12} /> {busy ? "Comparing…" : "Compare"}
      </button>
      {result && !result.error && (
        <div data-testid="twin-compare-result" className="mt-3 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Side r={result.a} val={result.a_value_label} higher={result.higher === "a"} testid="twin-compare-result-a" />
            <Side r={result.b} val={result.b_value_label} higher={result.higher === "b"} testid="twin-compare-result-b" />
          </div>
          <div data-testid="twin-compare-delta" className="border border-amber-200 bg-amber-50 rounded-lg p-3">
            {result.delta_label && result.higher !== "equal" && (
              <p className="text-[13px] text-zinc-900 font-semibold mb-1">£ delta: ~{result.delta_label} more at stake in Scenario {result.higher.toUpperCase()}</p>
            )}
            <p className="text-[12px] text-zinc-700">{result.summary}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <button data-testid="twin-compare-boardpack" onClick={boardPack}
                className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-2 py-1 rounded hover:bg-[var(--e360-primary)]/5">
                <FileDown size={12} /> Add to board pack
              </button>
              <input data-testid="twin-pin-name" value={pinName} onChange={(e) => setPinName(e.target.value)}
                placeholder="Name this comparison to pin it…"
                className="text-[12px] border border-zinc-300 rounded px-2 py-1 bg-white min-w-[220px]" />
              <button data-testid="twin-pin-save" onClick={pin} disabled={!pinName.trim() || pinning}
                className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] uppercase text-white bg-[var(--e360-primary)] px-2 py-1 rounded disabled:opacity-40">
                <Pin size={12} /> {pinning ? "Pinning…" : "Pin to pack"}
              </button>
            </div>
          </div>
        </div>
      )}
      {result?.error && <div className="text-[12px] text-red-600 mt-2">Could not compare — check both targets are selected.</div>}
    </div>
  );
}

function StreamCard({ s, onQuery }) {
  return (
    <button data-testid={`twin-stream-${s.id}`} onClick={() => onQuery("stream", s.id)}
      className="text-left border border-zinc-200 rounded-lg p-3 hover:border-[var(--e360-primary)] transition-colors">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[13px] font-semibold text-zinc-800">{s.name}</span>
        <span className="text-[11px] text-zinc-500">{s.owner}</span>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] text-zinc-500">{s.current_index ?? "—"} → <b className="text-zinc-800">{s.target_index ?? "—"}</b></span>
        {s.gap != null && <span className="text-[11px] text-[var(--e360-primary)]">gap {s.gap}</span>}
        <span className={`text-[11px] ${bandCls(s.target_band)}`}>{s.target_band}</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {(s.systems || []).slice(0, 4).map((sy) => (
          <span key={sy} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">{sy}</span>
        ))}
      </div>
    </button>
  );
}

function QueryPanel({ result, onClose }) {
  if (!result) return null;
  if (result.error) return <div data-testid="twin-query-result" className="text-[12px] text-red-600 py-2">Not found.</div>;
  const e = result.entity || {};
  return (
    <div data-testid="twin-query-result" className="border border-[var(--e360-primary)]/40 bg-[var(--e360-primary)]/5 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[13px] font-semibold text-zinc-800">{e.name || e.role || e.system}</span>
        <button onClick={onClose} className="text-[11px] text-zinc-400 hover:text-zinc-600">close</button>
      </div>
      {result.kind === "stream" && (
        <div className="space-y-1.5 text-[12px] text-zinc-600">
          <div><b>Owner:</b> {result.owner}</div>
          <div><b>Depends on:</b> {(result.depends_on || []).map((d) => d.stream).join(", ") || "—"}</div>
          <div><b>Depended on by:</b> {(result.dependents || []).join(", ") || "—"}</div>
          <div><b>Shared systems:</b> {(result.shared_systems || []).map((x) => `${x.system} (${x.count})`).join(", ") || "—"}</div>
          <div><b>KPIs:</b> {(e.kpis || []).join(" · ") || "—"}</div>
        </div>
      )}
      {result.kind === "role" && <div className="text-[12px] text-zinc-600"><b>Owns:</b> {(result.streams || []).join(", ")}</div>}
      {result.kind === "system" && <div className="text-[12px] text-zinc-600"><b>Used by:</b> {(result.streams || []).join(", ")}</div>}
    </div>
  );
}

function ScenarioRunner({ clientId, scenarios, options }) {
  const [sid, setSid] = useState(scenarios[0]?.id || "");
  const [target, setTarget] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const meta = scenarios.find((s) => s.id === sid);
  const targets = options[sid] || [];

  useEffect(() => { setTarget(""); setResult(null); }, [sid]);

  const run = useCallback(async () => {
    if (!sid || !target) return;
    setBusy(true); setResult(null);
    const params = sid === "gate_slip" ? { gate_ref: target, slip_days: 30 }
      : sid === "decision_delay" ? { decision_ref: target } : { stream_id: target };
    try {
      const { data } = await api.post(`/admin/enterprise360/clients/${clientId}/digital-twin/scenario`,
        { scenario: sid, params });
      setResult(data);
    } catch { setResult({ error: true }); }
    setBusy(false);
  }, [clientId, sid, target]);

  const downloadPdf = useCallback(async () => {
    const params = sid === "gate_slip" ? { gate_ref: target, slip_days: 30 }
      : sid === "decision_delay" ? { decision_ref: target } : { stream_id: target };
    try {
      const res = await api.post(`/admin/enterprise360/clients/${clientId}/digital-twin/scenario.pdf`,
        { scenario: sid, params }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Scenario_${sid}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  }, [clientId, sid, target]);

  return (
    <div data-testid="twin-scenarios" className="border border-zinc-200 rounded-lg p-3 mt-6">
      <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] mb-2">
        <Zap size={12} className="inline mr-1" /> What-if scenarios
      </div>
      <div className="flex gap-2 flex-wrap items-center mb-2">
        <select data-testid="twin-scenario-select" value={sid} onChange={(e) => setSid(e.target.value)}
          className="text-[12px] border border-zinc-300 rounded px-2 py-1.5 bg-white">
          {scenarios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select data-testid="twin-target-select" value={target} onChange={(e) => setTarget(e.target.value)}
          className="text-[12px] border border-zinc-300 rounded px-2 py-1.5 bg-white min-w-[200px]">
          <option value="">Select target…</option>
          {targets.map((t) => <option key={t.ref || t.id} value={t.ref || t.id}>{t.title || t.name}</option>)}
        </select>
        <button data-testid="twin-run-scenario" onClick={run} disabled={!target || busy}
          className="inline-flex items-center gap-1 text-[12px] px-3 py-1.5 rounded bg-[var(--e360-primary)] text-white disabled:opacity-40">
          <Play size={12} /> {busy ? "Running…" : "Run"}
        </button>
      </div>
      {meta && <p className="text-[11px] text-zinc-400 mb-2">{meta.describe}</p>}
      {result && !result.error && (
        <div data-testid="twin-scenario-result" className="border border-amber-200 bg-amber-50 rounded-lg p-3">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[13px] text-zinc-800 font-medium mb-1">{result.headline}</p>
            <button data-testid="twin-scenario-pdf" onClick={downloadPdf} title="Download board pack"
              className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-2 py-1 rounded shrink-0 hover:bg-[var(--e360-primary)]/5">
              <FileDown size={12} /> Board pack
            </button>
          </div>
          {(result.value_at_stake_exposed || result.value_at_stake_label) && <p className="text-[12px] text-[var(--e360-primary)] font-semibold">Value at stake: ~{result.value_at_stake_exposed || result.value_at_stake_label}<ConfidenceBadge conf={result.confidence} /></p>}
          {(result.downstream_gates || []).length > 0 && <p className="text-[11px] text-zinc-500 mt-1">Downstream gates: {result.downstream_gates.join(", ")}</p>}
          {(result.dependents || []).length > 0 && <p className="text-[11px] text-zinc-500 mt-1">Dependent streams: {result.dependents.join(", ")}</p>}
        </div>
      )}
      {result?.error && <div className="text-[12px] text-red-600">Could not run scenario.</div>}
    </div>
  );
}

function TopExposure({ clientId }) {
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(12);
  const [editPlan, setEditPlan] = useState(false);
  const [planGbp, setPlanGbp] = useState("");
  const [planDate, setPlanDate] = useState("");
  const [savingPlan, setSavingPlan] = useState(false);

  const reload = useCallback(() => {
    api.get(`/admin/enterprise360/clients/${clientId}/top-exposure?weeks=${weeks}`)
      .then(({ data }) => setData(data)).catch(() => setData({}));
  }, [clientId, weeks]);
  useEffect(() => { reload(); }, [reload]);

  const openPlanEditor = useCallback(() => {
    const pt = data?.trend?.plan_target;
    setPlanGbp(pt?.source === "custom" && pt?.target_gbp ? String(pt.target_gbp) : "");
    setPlanDate(pt?.source === "custom" && pt?.target_date ? pt.target_date : "");
    setEditPlan(true);
  }, [data]);

  const savePlan = useCallback(async (clear) => {
    setSavingPlan(true);
    try {
      const body = clear ? { target_gbp: null, target_date: null }
        : { target_gbp: planGbp === "" ? null : Number(planGbp), target_date: planDate || null };
      await api.put(`/admin/enterprise360/clients/${clientId}/exposure-plan`, body);
      setEditPlan(false); reload();
    } catch { /* ignore */ }
    setSavingPlan(false);
  }, [clientId, planGbp, planDate, reload]);

  if (!data || (!data.total_exposure_label && !(data.ranked || []).length)) return null;
  const levers = (data.ranked || []).slice(0, 3);
  const hasSeries = (data.trend?.series || []).length >= 2;
  const planTarget = data.trend?.plan_target;
  return (
    <div data-testid="twin-top-exposure" className="border border-[var(--e360-primary)]/30 bg-[var(--e360-primary)]/5 rounded-lg p-4 mb-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] mb-1">
            <ShieldAlert size={12} className="inline mr-1" /> Total at risk right now
          </div>
          {data.total_exposure_label ? (
            <div className="flex items-center flex-wrap">
              <span data-testid="twin-total-exposure" className="text-3xl font-bold text-[var(--e360-primary)]">~{data.total_exposure_label}</span>
              <ConfidenceBadge conf={data.total_exposure_confidence} />
              <TrendPill trend={data.trend} />
              <Sparkline series={data.trend?.series} plan={data.trend?.plan} />
              {data.trend?.vs_plan && (
                <span data-testid="twin-vs-plan" className={`text-[10.5px] font-semibold ml-1.5 ${data.trend.vs_plan === "ahead" ? "text-emerald-700" : "text-red-700"}`}>
                  ({data.trend.vs_plan} of plan)
                </span>
              )}
              {hasSeries && (
                <span data-testid="twin-trend-range" className="inline-flex items-center border border-[var(--e360-primary)]/30 rounded ml-2 overflow-hidden">
                  {[4, 12].map((w) => (
                    <button key={w} data-testid={`twin-trend-range-${w}`} onClick={() => setWeeks(w)}
                      className={`text-[10.5px] font-semibold px-2 py-0.5 ${weeks === w ? "bg-[var(--e360-primary)] text-white" : "text-[var(--e360-primary)] hover:bg-[var(--e360-primary)]/10"}`}>
                      {w}w
                    </button>
                  ))}
                </span>
              )}
              {hasSeries && (
                <button data-testid="twin-edit-plan" onClick={openPlanEditor}
                  className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-[var(--e360-primary)] border border-[var(--e360-primary)]/40 rounded px-2 py-0.5 ml-2 hover:bg-[var(--e360-primary)]/10">
                  <Target size={11} /> Edit plan
                </button>
              )}
            </div>
          ) : <div className="text-[13px] text-zinc-500">No £ base set — exposure ranked by impacted items.</div>}
          {hasSeries && planTarget && (
            <div data-testid="twin-plan-target" className="text-[10.5px] text-zinc-500 mt-1">
              {planTarget.source === "custom"
                ? <>Plan target: <b>~{planTarget.target_label}</b>{planTarget.target_date && <> by {planTarget.target_date}</>} (board-set).</>
                : <>Plan target: ~{planTarget.target_label} (default — halve exposure over the window). Set your own with <b>Edit plan</b>.</>}
            </div>
          )}
          {editPlan && (
            <div data-testid="twin-plan-editor" className="mt-2 flex flex-wrap items-end gap-2 bg-white border border-zinc-200 rounded p-2.5">
              <label className="text-[11px] text-zinc-600">Target £
                <input data-testid="twin-plan-gbp" type="number" min="0" value={planGbp} onChange={(e) => setPlanGbp(e.target.value)}
                  placeholder="e.g. 20000000" className="block text-[12px] border border-zinc-300 rounded px-2 py-1 bg-white min-w-[150px]" />
              </label>
              <label className="text-[11px] text-zinc-600">By date
                <input data-testid="twin-plan-date" type="date" value={planDate} onChange={(e) => setPlanDate(e.target.value)}
                  className="block text-[12px] border border-zinc-300 rounded px-2 py-1 bg-white" />
              </label>
              <button data-testid="twin-plan-save" onClick={() => savePlan(false)} disabled={savingPlan}
                className="text-[11px] font-semibold text-white bg-[var(--e360-primary)] rounded px-3 py-1.5 disabled:opacity-40">{savingPlan ? "Saving…" : "Save plan"}</button>
              <button data-testid="twin-plan-clear" onClick={() => savePlan(true)} disabled={savingPlan}
                className="text-[11px] text-zinc-500 hover:text-zinc-800 px-2 py-1.5">Reset to default</button>
              <button data-testid="twin-plan-cancel" onClick={() => setEditPlan(false)} className="text-[11px] text-zinc-400 hover:text-zinc-700 px-1">Cancel</button>
            </div>
          )}
          <div className="text-[11px] text-zinc-500 mt-1">Across {data.count || 0} open exposure(s) — each benefit counted once, straight from the live registers.</div>
          {data.total_exposure_label && data.total_exposure_confidence?.tier === "indicative" && (
            <div data-testid="twin-committed-nudge" className="mt-2 flex flex-wrap items-center gap-2 text-[11.5px] text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-2.5 py-1.5">
              <span>
                {data.indicative_benefit_count > 0
                  ? <><b data-testid="twin-indicative-count">{data.indicative_benefit_count}</b> of {data.exposed_benefit_count} benefit(s) behind these figures {data.indicative_benefit_count === 1 ? "is" : "are"} still <b>Indicative</b>{data.indicative_value_label && <> — <b data-testid="twin-indicative-value">~{data.indicative_value_label}</b> of value at stake</>}. Mark the biggest Committed/Validated to firm up the board numbers.</>
                  : <>These figures are <b>Indicative</b>. Mark benefits Committed/Validated to make the board numbers more defensible.</>}
              </span>
              <button data-testid="twin-goto-benefits" onClick={() => { try { window.dispatchEvent(new CustomEvent("e360-hub-goto-tab", { detail: "benefits" })); } catch { /* noop */ } }}
                className="underline font-semibold hover:text-indigo-950">Open Benefits →</button>
            </div>
          )}
        </div>
      </div>
      {levers.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] font-semibold text-zinc-500 mb-1.5"><TrendingUp size={12} className="inline mr-1" /> Biggest levers — unblock these this week</div>
          <ol data-testid="twin-biggest-levers" className="space-y-1.5">
            {levers.map((l, i) => (
              <li key={l.ref || i} data-testid={`twin-lever-${i}`} className="flex items-start gap-2 text-[12px]">
                <span className="shrink-0 w-5 h-5 rounded-full bg-[var(--e360-primary)] text-white text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                <span className="text-zinc-700">
                  <b className="text-zinc-900">{l.title || l.ref}</b>
                  {l.value_at_stake_label && <span className="text-[var(--e360-primary)] font-semibold ml-1">~{l.value_at_stake_label}</span>}
                  <ConfidenceBadge conf={l.confidence} />
                  <span className="block text-[11px] text-zinc-500">{l.headline}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
      <ConfidenceLegend />
    </div>
  );
}

export default function DigitalTwin({ clientId }) {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState(null);
  const [err, setErr] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    api.get(`/admin/enterprise360/clients/${clientId}/digital-twin`).then(({ data }) => setData(data)).catch(() => setErr(true));
  }, [clientId]);

  useEffect(() => {
    try { if (localStorage.getItem(TOUR_KEY) !== "true") setTourOpen(true); } catch { /* noop */ }
  }, []);

  const runQuery = useCallback((kind, key) => {
    api.post(`/admin/enterprise360/clients/${clientId}/digital-twin/query`, { kind, key })
      .then(({ data }) => setQuery(data)).catch(() => {});
  }, [clientId]);

  if (err) return <div data-testid="twin-error" className="text-sm text-red-600 py-4">Failed to load digital twin — please retry.</div>;
  if (!data) return <div className="text-sm text-zinc-400 py-4">Loading digital twin…</div>;
  const sm = data.summary || {};

  return (
    <div data-testid="digital-twin">
      <TwinTour open={tourOpen} setOpen={setTourOpen} />
      <div className="flex items-start justify-between gap-3 mb-4">
        <p className="text-[12px] text-zinc-500 max-w-2xl">
          <Boxes size={12} className="inline mr-1" /> A queryable model of the target operating model — streams, owners, systems and dependencies — with deterministic what-if scenarios grounded in the live evidence graph.
        </p>
        <button data-testid="twin-tour-open" onClick={() => setTourOpen(true)}
          className="inline-flex items-center gap-1 text-[11px] text-[var(--e360-primary)] border border-[var(--e360-primary)] px-2 py-1 rounded shrink-0 hover:bg-[var(--e360-primary)]/5">
          <HelpCircle size={12} /> Take the tour
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {[["Value streams", sm.streams, Boxes], ["Owners", sm.owners, Users], ["Systems", sm.systems, Cpu], ["Dependencies", sm.dependencies, GitBranch]].map(([label, val, Icon]) => (
          <div key={label} className="border border-zinc-200 rounded-lg p-2.5">
            <div className="text-[11px] text-zinc-400"><Icon size={11} className="inline mr-1" />{label}</div>
            <div className="text-[18px] font-semibold text-zinc-800">{val ?? "—"}</div>
          </div>
        ))}
      </div>
      {sm.avg_current_index != null && (
        <div className="text-[12px] text-zinc-500 mb-4">Portfolio maturity: <b>{sm.avg_current_index}</b> → target <b className="text-[var(--e360-primary)]">{sm.avg_target_index}</b> · {sm.shared_systems} shared system(s)</div>
      )}

      <QueryPanel result={query} onClose={() => setQuery(null)} />

      <TopExposure clientId={clientId} />

      <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] mb-2">Target operating model — click a stream to interrogate</div>
      <div className="grid sm:grid-cols-2 gap-2">
        {data.streams.map((s) => <StreamCard key={s.id} s={s} onQuery={runQuery} />)}
      </div>

      <ScenarioRunner clientId={clientId} scenarios={data.scenarios || []} options={data.scenario_options || {}} />
      <ScenarioCompare clientId={clientId} scenarios={data.scenarios || []} options={data.scenario_options || {}} />
    </div>
  );
}
